import axios from '../utils/axiosClient';
import endpoints from './endpoints';
import {
  TransferValidationRequest,
  TransferValidationResponse,
  CheckDuplicateRequest,
  CheckDuplicateResponse,
  CheckDuplicateSwiftRequest,
} from '../interfaces/transfer';

const {
  newTransfer,
  newCsvTransfer,
  getRatesReport,
  newInternationalTransfer,
  getTransfers,
  getSwiftTransfers,
  editRemoveTransfer,
  validInternationalTransfer,
  calculateTransfersFee,
  checkDuplicateTransfer,
  checkDuplicateSwiftTransfer,
  getAccountInf,
  getAccountsInf,
  getSwiftFile,
} = endpoints;

const createTransfer = async (data: any, csvTransfers: boolean, idempotencyKey: string) => {
  const route = csvTransfers ? newCsvTransfer : newTransfer;
  const headers: Record<string, string> = { 'X-Idempotency-Key': idempotencyKey };
  if (data instanceof FormData) headers['Content-Type'] = 'multipart/form-data';

  try {
    const response = await axios.post(route, data, { headers });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const editTransfer = async (id: string, data: any) => {
  try {
    const response = await axios.put(editRemoveTransfer(id), data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const createInternationalTransfer = async (data: any, idempotencyKey: string) => {
  try {
    const response = await axios.post(newInternationalTransfer, data, {
      headers: { 'Content-Type': 'multipart/form-data', 'X-Idempotency-Key': idempotencyKey },
    });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const validateInternationalTransfer = async (data: any) => {
  try {
    const response = await axios.post(validInternationalTransfer, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const loadTransfers = async (params: any) => {
  try {
    const response = await axios.get(getTransfers, { params });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const loadSwiftTransfers = async (params: any) => {
  try {
    const response = await axios.get(getSwiftTransfers, { params });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const getAccountInfo = async (accountCode: string) => {
  try {
    const response = await axios.get(getAccountInf, { params: { accountCode } });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const getAccountsInfo = async (transferRequests: TransferValidationRequest[]): Promise<TransferValidationResponse> => {
  try {
    const response = await axios.post(getAccountsInf, { transferRequests });
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const removeTransfer = async (id: string) => {
  try {
    const response = await axios.delete(editRemoveTransfer(id));
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const loadRatesReport = async (params: any) => {
  const queryParams = new URLSearchParams(params).toString();

  try {
    const response = await axios.get(`${getRatesReport}?${queryParams}`, { responseType: 'blob' });
    const fileBlob = new Blob([response.data], { type: response.headers['content-type'] });
    const fileUrl = URL.createObjectURL(fileBlob);

    return window.open(fileUrl, '_blank');
  } catch (error: any) {
    console.log(error);
    return Promise.reject(error.response);
  }
};

const calculateFee = async (data: any) => {
  try {
    const response = await axios.post(calculateTransfersFee, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const checkDuplicate = async (data: CheckDuplicateRequest): Promise<CheckDuplicateResponse> => {
  try {
    const response = await axios.post(checkDuplicateTransfer, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const checkDuplicateSwift = async (data: CheckDuplicateSwiftRequest): Promise<CheckDuplicateResponse> => {
  try {
    const response = await axios.post(checkDuplicateSwiftTransfer, data);
    return response.data;
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const fetchSwiftFile = async (id: string, params: any) => {
  try {
    const response = await axios.get(getSwiftFile(id), { params, responseType: 'blob' });
    const fileBlob = new Blob([response.data], { type: response.headers['content-type'] });
    return URL.createObjectURL(fileBlob);
  } catch (error: any) {
    return Promise.reject(error.response);
  }
};

const transferServices = {
  createTransfer,
  createInternationalTransfer,
  validateInternationalTransfer,
  loadTransfers,
  loadSwiftTransfers,
  removeTransfer,
  editTransfer,
  loadRatesReport,
  calculateFee,
  checkDuplicate,
  checkDuplicateSwift,
  getAccountInfo,
  getAccountsInfo,
  fetchSwiftFile,
};

export default transferServices;
